import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import { categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredComics = useMemo(() => {
    if (!slug) return [];
    return getComicsByCategory(slug);
  }, [slug]);

  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const paginatedComics = filteredComics.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const pageTitle = category
    ? `${category.name}韩漫 - 非麻瓜漫画`
    : '漫画分类 - 非麻瓜漫画';
  const pageDescription = category
    ? `非麻瓜漫画的${category.name}分类，${category.description}。海量免费${category.name}韩漫在线观看，支持下拉式阅读体验。`
    : '非麻瓜漫画全部分类，探索海量免费韩漫资源。';

  useEffect(() => {
    document.title = pageTitle;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    }
    return () => {
      document.title = '非麻瓜漫画-免费韩漫在线阅读网站_最新韩国漫画下拉式观看';
    };
  }, [pageTitle, pageDescription]);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 font-heading mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '漫画分类' },
              { label: category.name },
            ]}
          />

          {/* Category Header */}
          <div className="py-8 text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
              {category.name}韩漫
            </h1>
            <p className="text-sm text-foreground-600 max-w-lg mx-auto">
              {category.description}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-8 justify-center">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Comic Grid */}
          {filteredComics.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {paginatedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-600">该分类下暂无漫画</p>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": pageTitle,
          "description": pageDescription,
          "url": `https://feimagua.com/category/${slug}`,
          "inLanguage": "zh-Hans"
        })
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://feimagua.com" },
            { "@type": "ListItem", "position": 2, "name": category.name, "item": `https://feimagua.com/category/${slug}` }
          ]
        })
      }} />
    </>
  );
}